import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { HeaderFieldComponent, HeaderGroupComponent, HeaderItemComponent } from '@vectronic/ui';
import { InternationalDateFormat, utcMoment } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import { Moment } from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CameraScheduleService } from '../../../services/camera-schedule.service';
import { RemoteCommandsService } from '../../../services/commands/remote-commands.service';
import { CameraSliderComponent } from '../camera-slider/camera-slider.component';
import {
  ADDITIONAL_CONDITION_TRIGGER_OPTIONS,
  AdditionalConditionTriggerType,
  MAX_DATE,
  MIN_DATE,
  MODE_OPTIONS,
  REPEAT_INTERVAL_OPTIONS,
  TIME_TYPE_OPTIONS,
  TRIGGER_OPTIONS,
  VIT_TRIGGER_OPTIONS,
} from './camera-rule-config';
import {
  calculateWeekdays,
  convertCycleTimeToRepeatIntervalDay,
  convertCycleTimeToRepeatIntervalHour,
  convertCycleTimeToRepeatIntervalMinute,
  convertMainTriggerToGpsSkipCount,
  convertMainTriggerToProximityActiveTimeDay,
  convertMainTriggerToProximityActiveTimeHour,
  convertMainTriggerToProximityActiveTimeMinute,
  convertMainTriggerToTimeIntervalDay,
  convertMainTriggerToTimeIntervalHour,
  convertMainTriggerToTimeIntervalMinute,
  convertMainTriggerToVitExpected,
  convertPeriodLengthToRepeatIntervalType,
  convertPictureIntervalToMinutes,
  convertPictureIntervalToSeconds,
  convertSizeToPhotoCount,
  convertSizeToVideoLengthHours,
  convertSizeToVideoLengthMinutes,
  convertSizeToVideoLengthSeconds,
  convertStartAndOnTimeToEndHour,
  convertStartAndOnTimeToEndMinute,
  convertStartAndOnTimeToEndWeekday,
  convertStartAndOnTimeToStartHour,
  convertStartAndOnTimeToStartMinute,
  convertStartTimeToStartWeekday,
  getOffset,
  isDaily,
  isWeekly,
} from './camera-rule-utils';

@Component({
  selector: 'rc-camera-rule',
  templateUrl: './camera-rule.component.html',
  styleUrls: ['./camera-rule.component.scss'],
  imports: [
    AsyncPipe,
    MatCard,
    MatCardTitle,
    MatIconButton,
    MatIcon,
    HeaderGroupComponent,
    HeaderItemComponent,
    HeaderFieldComponent,
    MatError,
    MatDivider,
    MatCardContent,
    CameraSliderComponent,
    MatCardActions,
    MatTooltip,
    MatButton,
  ],
  providers: [provideMomentDateAdapter(InternationalDateFormat, { useUtc: true })],
})
export class CameraRuleComponent implements OnInit, OnDestroy {
  cameraScheduleService = inject(CameraScheduleService);
  remoteCommandsService = inject(RemoteCommandsService);

  showDuration = false;
  showStart = false;
  showEnd = false;
  showMode = false;
  showTrigger = false;
  showRepeat = false;
  lastStartWeekday = '';
  lastEndWeekday = '';
  weekdays: {
    value: string;
    label: string;
  }[];
  rulesSubscription: Subscription;
  rule = signal<dsDtos.CameraScheduleRule>({
    startTime:
      utcMoment()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISOString()
        .split('.')[0] + 'Z',
    endTime:
      utcMoment()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .add({ year: 25 })
        .toISOString()
        .split('.')[0] + 'Z',
    cycleTime: 1440 * 60,
    onTime: 1440 * 60,
    cameraScheduleRuleMainTrigger: 'TIMER',
    mainTriggerValue: 120,
    ruleOnlyValidOnProximityState: false,
    ruleOnlyValidOutsideVirtualFence: false,
    ruleOnlyValidVitNotExpelledState: false,
    ruleOnlyValidVitNoContactState: false,
    ruleOnlyValidVitContactState: false,
    ruleOnlyValidVitExpelledState: false,
    ruleOnlyValidInsideVirtualFence: false,
    ruleOnlyValidOnNoProximityState: false,
  });

  readonly = input<boolean>();
  duration = input<number>();
  index = input<number>();

  private indexReaction = toObservable(this.index)
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.initialUiUpdate();
    });

  videoFormatsOptions = input<{ value: number; label: string }[]>();
  photoFormatsOptions = input<{ value: number; label: string }[]>();
  order = input<number>();
  hideChangedSchedules = input<boolean>();
  isRulesDeletable = input<boolean>();
  hideCameraFormat = input<boolean>();
  hasHighActivityCameraTrigger = input<boolean>(false);

  remove = output<number>();

  startWeekdayFormControl = new FormControl('');
  endWeekdayFormControl = new FormControl('');
  repeatIntervalTypeFormControl = new FormControl('daily');
  endDateFormControl = new FormControl<Moment | string | Date>(this.rule().endTime);
  startDateFormControl = new FormControl<Moment | string | Date>(this.rule().startTime);
  startHourFormControl = new FormControl<number>(0);
  startMinuteFormControl = new FormControl<number>(0);
  endHourFormControl = new FormControl<number>(24);
  endMinuteFormControl = new FormControl<number>(0);
  photoCountFormControl = new FormControl(1);
  gpsSkipCountFormControl = new FormControl(0);
  vitExpectedFormControl = new FormControl(undefined);
  additionalConditionOptionsFormControl = new FormControl<AdditionalConditionTriggerType[]>(
    undefined,
  );
  timeTypeFormControl = new FormControl('permanent');
  modeFormControl = new FormControl<dsEnums.CameraScheduleRuleType>(undefined);
  triggerFormControl = new FormControl<dsEnums.CameraScheduleRuleMainTrigger>('TIMER');
  repeatIntervalDayFormControl = new FormControl(1);
  repeatIntervalHourFormControl = new FormControl(0);
  repeatIntervalMinuteFormControl = new FormControl(0);
  cameraFormatFormControl = new FormControl(undefined);
  priorityFormControl = new FormControl(1);
  videoLengthHourFormControl = new FormControl(0);
  videoLengthMinuteFormControl = new FormControl(0);
  videoLengthSecondFormControl = new FormControl(10);
  photoIntervalMinuteFormControl = new FormControl(0);
  photoIntervalSecondFormControl = new FormControl(15);
  timeIntervalDayFormControl = new FormControl(0);
  timeIntervalHourFormControl = new FormControl(2);
  timeIntervalMinuteFormControl = new FormControl(0);
  proximityActiveTimeFormControlDayFormControl = new FormControl(0);
  proximityActiveTimeFormControlHourFormControl = new FormControl(0);
  proximityActiveTimeFormControlMinuteFormControl = new FormControl(0);

  cameraFormatsOptions = signal<{ value: number; label: string }[]>([]);

  priorityOptions = [
    { value: 1, label: 'Standard' },
    { value: 2, label: 'High' },
  ];

  cameraFormatIsClosed = new BehaviorSubject(false);
  removeSubscription: Subscription;

  ngOnInit() {
    this.cameraFormatsOptions.set(this.photoFormatsOptions());
    this.updateWeekdays();
    this.rulesSubscription = this.cameraScheduleService.rules$.subscribe((rules) => {
      if (!this.hideChangedSchedules()) {
        const rule = rules[this.index()];
        this.calculateStartEnd(rule);
        this.initialUiUpdate();
        this.cameraScheduleService.checkErrors();
      }
    });
    this.removeSubscription = this.cameraScheduleService?.isRemovedSchedule$.subscribe(
      (isRemoved) => {
        if (isRemoved && !this.hideChangedSchedules()) {
          this.resetRule();
        }
      },
    );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  initialUiUpdate() {
    const idx = this.index();
    const cameraRule = this.hideChangedSchedules()
      ? this.cameraScheduleService.defaultRules$.value[idx]
      : this.cameraScheduleService.rules$.value[idx];
    if (cameraRule && !cameraRule.onTime) {
      cameraRule.onTime = 1440 * 60;
    }
    if (cameraRule && !cameraRule.cycleTime) {
      cameraRule.cycleTime = 1440 * 60;
    }
    if (cameraRule && !cameraRule.startTime) {
      cameraRule.startTime =
        utcMoment()
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .toISOString()
          .split('.')[0] + 'Z';
    }
    if (cameraRule && !cameraRule.endTime) {
      cameraRule.endTime =
        utcMoment()
          .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
          .add({ year: 25 })
          .toISOString()
          .split('.')[0] + 'Z';
    }
    if (cameraRule && Object.keys(cameraRule).length) {
      this.rule.set(cloneDeep(cameraRule));
      this.startWeekdayFormControl.setValue(
        convertStartTimeToStartWeekday(cameraRule.startTime, cameraRule.cycleTime),
      );
      this.endWeekdayFormControl.setValue(
        convertStartAndOnTimeToEndWeekday(
          cameraRule.startTime,
          cameraRule.onTime,
          cameraRule.cycleTime,
        ),
      );
      this.endHourFormControl.setValue(
        convertStartAndOnTimeToEndHour(
          cameraRule.startTime,
          cameraRule.onTime,
          cameraRule.cycleTime,
        ),
      );
      this.endMinuteFormControl.setValue(
        convertStartAndOnTimeToEndMinute(cameraRule.startTime, cameraRule.onTime),
      );
      this.startHourFormControl.setValue(convertStartAndOnTimeToStartHour(cameraRule.startTime));
      this.startMinuteFormControl.setValue(
        convertStartAndOnTimeToStartMinute(cameraRule.startTime),
      );
      this.repeatIntervalTypeFormControl.setValue(
        convertPeriodLengthToRepeatIntervalType(cameraRule.cycleTime),
      );
      if (
        utcMoment(cameraRule.startTime).add({ year: 25 }).isBefore(utcMoment(cameraRule.endTime))
      ) {
        this.timeTypeFormControl.setValue('permanent');
      } else {
        this.timeTypeFormControl.setValue('temporary');
      }
      this.startDateFormControl.setValue(cameraRule.startTime);
      this.endDateFormControl.setValue(cameraRule.endTime);
      this.photoCountFormControl.setValue(
        convertSizeToPhotoCount(cameraRule.size, cameraRule.cameraScheduleRuleType),
      );
      this.gpsSkipCountFormControl.setValue(
        convertMainTriggerToGpsSkipCount(
          cameraRule.mainTriggerValue,
          cameraRule.cameraScheduleRuleMainTrigger,
        ),
      );
      this.vitExpectedFormControl.setValue(
        convertMainTriggerToVitExpected(
          cameraRule.mainTriggerValue,
          cameraRule.cameraScheduleRuleMainTrigger,
        ),
      );
      this.modeFormControl.setValue(cameraRule.cameraScheduleRuleType);
      if (cameraRule.cameraScheduleRuleType === 'VIDEO') {
        this.cameraFormatsOptions.set(this.videoFormatsOptions());
      } else {
        this.cameraFormatsOptions.set(this.photoFormatsOptions());
      }

      this.priorityFormControl.setValue(cameraRule.videoRecordMode);
      if (
        cameraRule.cameraScheduleRuleMainTrigger === 'HIGH_ACTIVITY' &&
        !this.hasHighActivityCameraTrigger()
      ) {
        this.triggerFormControl.setValue(null);
        const updatedTrigger = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
        updatedTrigger.cameraScheduleRuleMainTrigger = null;
        updatedTrigger.mainTriggerValue = null;
        this.rule.set(updatedTrigger);
        this.updateCameraRule();
      } else {
        this.triggerFormControl.setValue(cameraRule.cameraScheduleRuleMainTrigger);
      }
      this.repeatIntervalDayFormControl.setValue(
        convertCycleTimeToRepeatIntervalDay(cameraRule.cycleTime),
      );
      this.repeatIntervalHourFormControl.setValue(
        convertCycleTimeToRepeatIntervalHour(cameraRule.cycleTime),
      );
      this.repeatIntervalMinuteFormControl.setValue(
        convertCycleTimeToRepeatIntervalMinute(cameraRule.cycleTime),
      );
      this.videoLengthHourFormControl.setValue(convertSizeToVideoLengthHours(cameraRule.size));
      this.videoLengthMinuteFormControl.setValue(convertSizeToVideoLengthMinutes(cameraRule.size));
      this.videoLengthSecondFormControl.setValue(convertSizeToVideoLengthSeconds(cameraRule.size));
      this.cameraFormatFormControl.setValue(cameraRule.cameraFormatId);
      this.photoIntervalMinuteFormControl.setValue(
        convertPictureIntervalToMinutes(cameraRule.pictureInterval),
      );
      this.photoIntervalSecondFormControl.setValue(
        convertPictureIntervalToSeconds(cameraRule.pictureInterval),
      );
      this.timeIntervalDayFormControl.setValue(
        convertMainTriggerToTimeIntervalDay(cameraRule.mainTriggerValue),
      );
      this.timeIntervalHourFormControl.setValue(
        convertMainTriggerToTimeIntervalHour(cameraRule.mainTriggerValue),
      );
      this.timeIntervalMinuteFormControl.setValue(
        convertMainTriggerToTimeIntervalMinute(cameraRule.mainTriggerValue),
      );
      this.proximityActiveTimeFormControlDayFormControl.setValue(
        convertMainTriggerToProximityActiveTimeDay(cameraRule.mainTriggerValue),
      );
      this.proximityActiveTimeFormControlHourFormControl.setValue(
        convertMainTriggerToProximityActiveTimeHour(cameraRule.mainTriggerValue),
      );
      this.proximityActiveTimeFormControlMinuteFormControl.setValue(
        convertMainTriggerToProximityActiveTimeMinute(cameraRule.mainTriggerValue),
      );
      const onProximity: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidOnProximityState ? 'proximity' : undefined;
      const onNoProximity: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidOnNoProximityState ? 'no proximity' : undefined;
      const insideVirtualFence: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidInsideVirtualFence ? 'inside virtual fence' : undefined;
      const outsideVirtualFence: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidOutsideVirtualFence ? 'outside virtual fence' : undefined;
      const vitNotExpelled: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidVitNotExpelledState ? 'vit implanted' : undefined;
      const vitExpelled: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidVitExpelledState ? 'vit expelled' : undefined;
      const vitContact: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidVitContactState ? 'vit contact' : undefined;
      const onVitNoContact: AdditionalConditionTriggerType | undefined =
        cameraRule.ruleOnlyValidVitNoContactState ? 'vit no contact' : undefined;
      const newAdditional = [
        onProximity,
        onNoProximity,
        insideVirtualFence,
        outsideVirtualFence,
        vitNotExpelled,
        vitExpelled,
        vitContact,
        onVitNoContact,
      ].filter(Boolean);

      if (
        JSON.stringify(newAdditional) !==
        JSON.stringify(this.additionalConditionOptionsFormControl.value)
      ) {
        this.additionalConditionOptionsFormControl.setValue(newAdditional);
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  calculateStartEnd(rule: dsDtos.CameraScheduleRule) {
    if (!rule) {
      this.startWeekdayFormControl.setValue(undefined);
      this.endWeekdayFormControl.setValue(undefined);
      this.startHourFormControl.setValue(0);
      this.startMinuteFormControl.setValue(0);
      this.endHourFormControl.setValue(24);
      this.endMinuteFormControl.setValue(0);
      return;
    }
    const offset = getOffset(rule.startTime);
    if (isWeekly(rule.cycleTime) || isDaily(rule.cycleTime)) {
      this.startWeekdayFormControl.setValue(
        isWeekly(rule.cycleTime) ? this.weekdays[Math.floor(offset / 1440)]?.value : undefined,
      );
      this.startHourFormControl.setValue(Math.floor((offset % 1440) / 60));
      this.startMinuteFormControl.setValue(offset % 60);
      if (offset + rule.onTime / 60 === 10080) {
        this.endWeekdayFormControl.setValue(
          isWeekly(rule.cycleTime) ? this.weekdays[0]?.value : undefined,
        );
        this.endHourFormControl.setValue(0);
        this.endMinuteFormControl.setValue(0);
      } else if (offset + rule.onTime / 60 === 1440) {
        this.endWeekdayFormControl.setValue(
          isWeekly(rule.cycleTime) ? this.weekdays[0]?.value : undefined,
        );
        this.endHourFormControl.setValue(24);
        this.endMinuteFormControl.setValue(0);
      } else {
        const endTotal = offset + rule.onTime / 60;
        const weekdayIndex =
          endTotal > 10080
            ? Math.min(Math.floor((endTotal - 10080) / 1440), 6)
            : Math.min(Math.floor(endTotal / 1440), 6);
        this.endWeekdayFormControl.setValue(
          isWeekly(rule.cycleTime) ? this.weekdays[weekdayIndex]?.value : undefined,
        );
        this.endHourFormControl.setValue(Math.floor((endTotal % 1440) / 60));
        this.endMinuteFormControl.setValue(endTotal % 60);
      }
    } else {
      this.startHourFormControl.setValue(Math.floor(offset / 60));
      this.startMinuteFormControl.setValue(offset % 60);
      this.endHourFormControl.setValue(Math.floor((offset + rule.onTime / 60) / 60));
      this.endMinuteFormControl.setValue((offset + rule.onTime / 60) % 60);
    }
  }

  updateWeekdays() {
    this.weekdays = calculateWeekdays(this.startDateFormControl.value);
    if (!this.startWeekdayFormControl.value) {
      this.startWeekdayFormControl.setValue(this.weekdays[0]?.value);
    }
    if (!this.endWeekdayFormControl.value) {
      this.endWeekdayFormControl.setValue(this.weekdays[0]?.value);
    }
  }

  onTimeTypeChanged(value: string | any) {
    this.timeTypeFormControl.setValue(value);
    if (value === 'permanent') {
      const start = utcMoment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      const end = utcMoment()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .add({ year: 25 });
      this.startDateFormControl.setValue(start);
      this.endDateFormControl.setValue(
        utcMoment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).add({ year: 25 }),
      );
      const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
      updated.startTime = start.toISOString().split('.')[0] + 'Z';
      updated.endTime = end.toISOString().split('.')[0] + 'Z';
      this.rule.set(updated);
      this.updateCameraRule();
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    } else {
      this.startDateFormControl.setValue(undefined);
      this.endDateFormControl.setValue(undefined);
      const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
      updated.startTime = undefined;
      updated.endTime = undefined;
      this.rule.set(updated);
    }
  }

  onStartDateChanged(datePicker: Date | any) {
    const start =
      utcMoment(datePicker)
        .set({
          hours: this.startHourFormControl.value,
          minutes: this.startMinuteFormControl.value,
        })
        .toISOString()
        .split('.')[0] + 'Z';
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.startTime = start;
    if (!updated.endTime) {
      updated.endTime = start;
    }
    this.rule.set(updated);
    this.updateCameraRule();
    if (!this.isEndDateAfterStartDate) {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: true,
      });
    } else {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    }
    this.updateWeekdays();
  }

  onEndDateChanged(datePicker: Date | any) {
    const end =
      utcMoment(datePicker)
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .toISOString()
        .split('.')[0] + 'Z';
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.endTime = end;
    this.rule.set(updated);
    this.updateCameraRule();
    if (!this.isEndDateAfterStartDate) {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: true,
      });
    } else {
      this.endDateFormControl.setErrors({
        isEndDateAfterStartDate: null,
      });
      this.endDateFormControl.updateValueAndValidity();
    }
  }

  get isEndDateAfterStartDate() {
    return utcMoment(this.endDateFormControl.value).isSameOrAfter(
      utcMoment(this.startDateFormControl.value),
    );
  }

  getDurationStringInSecond(value: number) {
    if (value === 0) return '';
    const seconds = Number(value);
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (m && !s && !h && !d) {
      return `${m} min`;
    }
    if (s && !m && !h && !d) {
      return `${s} sec`;
    }
    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    const sDisplay = s > 0 ? s + 's ' : '';
    return (dDisplay + hDisplay + mDisplay + sDisplay).trim();
  }

  getDurationStringInMinute(value: number) {
    if (value === 0) return '';
    const seconds = Number(value);
    const d = Math.floor(seconds / (60 * 24));
    const h = Math.floor((seconds % (60 * 24)) / 60);
    const m = Math.floor(seconds % 60);
    if (m && !h && !d) {
      return `${m} min`;
    }
    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    return (dDisplay + hDisplay + mDisplay).trim();
  }

  onClickRule() {
    this.cameraScheduleService.toggleRule(this.index());
    if (!this.cameraScheduleService.isRuleExpanded[this.index()]) {
      this.showDuration = false;
      this.showStart = false;
      this.showEnd = false;
      this.showRepeat = false;
      this.showMode = false;
      this.showTrigger = false;
    }
  }

  getDateString(value: number) {
    if (value === 0) return '00:00';
    const minutes = value % 60;
    const hours = (value - minutes) / 60;
    const hourString = hours < 10 ? `0${hours}` : hours;
    const minuteString = minutes < 10 ? `0${minutes}` : minutes;
    return `${hourString}:${minuteString}`;
  }

  getStartString() {
    if (
      (!this.startHourFormControl.value && this.startHourFormControl.value != 0) ||
      (!this.startMinuteFormControl.value && this.startMinuteFormControl.value != 0) ||
      !this.startDateFormControl.value
    ) {
      return '';
    }
    const sum = this.startHourFormControl.value * 60 + this.startMinuteFormControl.value;
    if (isWeekly(this.rule().cycleTime)) {
      return `${this.startWeekdayFormControl.value} ${this.getDateString(sum)}`;
    }
    return this.getDateString(sum);
  }

  getEndString() {
    if (
      (!this.endHourFormControl.value && this.endHourFormControl.value != 0) ||
      (!this.endMinuteFormControl.value && this.endMinuteFormControl.value != 0)
    ) {
      return '';
    }
    const sum = this.endHourFormControl.value * 60 + this.endMinuteFormControl.value;
    if (isWeekly(this.rule().cycleTime)) {
      return `${this.endWeekdayFormControl.value} ${this.getDateString(sum)}`;
    }
    if (
      isDaily(this.rule().cycleTime) &&
      getOffset(this.rule().startTime) + this.rule().onTime / 60 > this.rule().cycleTime / 60
    ) {
      return `${this.getDateString(sum)} (+1d)`;
    }
    return this.getDateString(sum);
  }

  /**
   * Computes the on time (in seconds) from the start and end form controls.
   * An end before the start (or at the start) is treated as overlapping into the next cycle.
   */
  private getOnTimeFromControls(): number {
    const endHour = this.endHourFormControl.value ?? 0;
    const endMinute = this.endMinuteFormControl.value ?? 0;
    const startHour = this.startHourFormControl.value ?? 0;
    const startMinute = this.startMinuteFormControl.value ?? 0;
    if (isWeekly(this.rule().cycleTime)) {
      let endWeekdayIndex = Math.max(
        0,
        this.weekdays.findIndex((item) => item.value === this.endWeekdayFormControl.value),
      );
      const startWeekdayIndex = Math.max(
        0,
        this.weekdays.findIndex((item) => item.value === this.startWeekdayFormControl.value),
      );
      if (endWeekdayIndex === 0) endWeekdayIndex = 7;
      let endTotal = endWeekdayIndex * 1440 + endHour * 60 + endMinute;
      const startTotal = startWeekdayIndex * 1440 + startHour * 60 + startMinute;
      if (endTotal <= startTotal) {
        endTotal += 10080;
      }
      return (endTotal - startTotal) * 60;
    }
    const cycleMinutes = this.rule().cycleTime / 60;
    let endTotal = endHour * 60 + endMinute;
    const startTotal = startHour * 60 + startMinute;
    if (endTotal <= startTotal) {
      endTotal += cycleMinutes;
    }
    return (endTotal - startTotal) * 60;
  }

  updateStart() {
    if (!this.startWeekdayFormControl || !this.startHourFormControl || !this.startMinuteFormControl)
      return;
    const startTime =
      utcMoment(this.startDateFormControl.value)
        .set({
          hours: this.startHourFormControl.value,
          minutes: this.startMinuteFormControl.value,
        })
        .toISOString()
        .split('.')[0] + 'Z';
    const onTime = this.getOnTimeFromControls();
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.startTime = startTime;
    updated.onTime = onTime;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  updateEnd() {
    if (!this.endWeekdayFormControl || !this.endHourFormControl || !this.endMinuteFormControl)
      return;
    const onTime = this.getOnTimeFromControls();
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.onTime = onTime;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onModeChanged(value: dsEnums.CameraScheduleRuleType) {
    this.modeFormControl.setValue(value);
    if (value === 'VIDEO') {
      const size =
        (parseInt(this.videoLengthHourFormControl.value as any) || 0) * 3600 +
        (parseInt(this.videoLengthMinuteFormControl.value as any) || 0) * 60 +
        (parseInt(this.videoLengthSecondFormControl.value as any) || 0);
      const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
      updated.cameraScheduleRuleType = 'VIDEO';
      updated.size = size;
      updated.videoRecordMode = parseInt(this.priorityFormControl.value as any) || 1;
      this.rule.set(updated);
      this.updateCameraRule();
      this.cameraFormatsOptions.set(this.videoFormatsOptions());
    } else if (value === 'PICTURE') {
      if (!this.photoCountFormControl.value) {
        this.photoCountFormControl.setValue(1);
      }
      const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
      updated.cameraScheduleRuleType = 'PICTURE';
      updated.size = parseInt(this.photoCountFormControl.value as any);
      updated.videoRecordMode = undefined;
      this.rule.set(updated);
      this.updateCameraRule();
      this.cameraFormatsOptions.set(this.photoFormatsOptions());
    }
  }

  updatePhotoCount() {
    let size = parseInt(this.photoCountFormControl.value as any);
    if (size > 100) {
      size = 100;
      this.photoCountFormControl.setValue(100);
    }
    const updatedBase = cloneDeep(this.rule());
    updatedBase.size = size;
    const updated =
      updatedBase.pictureInterval && size !== 1
        ? updatedBase
        : ((): dsDtos.CameraScheduleRule => {
            const cp = cloneDeep(updatedBase);
            cp.pictureInterval = 15;
            return cp;
          })();
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onTriggerChanged(value: dsEnums.CameraScheduleRuleMainTrigger) {
    this.triggerFormControl.setValue(value);
    let v: number;
    let mainTriggerValue: number | undefined = this.rule().mainTriggerValue;
    let mainTrigger: dsEnums.CameraScheduleRuleMainTrigger =
      this.rule().cameraScheduleRuleMainTrigger;
    switch (value) {
      case 'GPS':
        v = parseInt(this.gpsSkipCountFormControl.value as any) || 0;
        mainTriggerValue = Math.max(
          this.minMainTriggerValue,
          Math.min(v, this.maxMainTriggerValue),
        );
        mainTrigger = 'GPS';
        break;
      case 'TIMER':
        v =
          (parseInt(this.timeIntervalDayFormControl.value as any) || 0) * 1440 +
          (parseInt(this.timeIntervalHourFormControl.value as any) || 0) * 60 +
          (parseInt(this.timeIntervalMinuteFormControl.value as any) || 0);
        mainTriggerValue = Math.max(
          this.minMainTriggerValue,
          Math.min(v, this.maxMainTriggerValue),
        );
        mainTrigger = 'TIMER';
        break;
      case 'VIT':
        v = parseInt(this.vitExpectedFormControl.value);
        if (v === 0 || isNaN(v)) {
          v = 1;
          this.vitExpectedFormControl.setValue(1);
        }
        mainTriggerValue = Math.max(
          this.minMainTriggerValue,
          Math.min(v, this.maxMainTriggerValue),
        );
        mainTrigger = 'VIT';
        break;
      case 'PROXIMITY_PRELIMANARY':
        v =
          (parseInt(this.proximityActiveTimeFormControlDayFormControl.value as any) || 0) * 1440 +
          (parseInt(this.proximityActiveTimeFormControlHourFormControl.value as any) || 0) * 60 +
          (parseInt(this.proximityActiveTimeFormControlMinuteFormControl.value as any) || 0);
        mainTriggerValue = Math.max(
          this.minMainTriggerValue,
          Math.min(v, this.maxMainTriggerValue),
        );
        mainTrigger = 'PROXIMITY_PRELIMANARY';
        break;
      case 'HIGH_ACTIVITY':
        mainTriggerValue = undefined;
        mainTrigger = 'HIGH_ACTIVITY';
        break;
    }
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.mainTriggerValue = mainTriggerValue;
    updated.cameraScheduleRuleMainTrigger = mainTrigger;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  updateGpsSkipCount() {
    const value = parseInt(this.gpsSkipCountFormControl.value as any) || 0;
    const mainTriggerValue = Math.max(
      this.minMainTriggerValue,
      Math.min(value, this.maxMainTriggerValue),
    );
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.mainTriggerValue = mainTriggerValue;
    this.rule.set(updated);
    this.gpsSkipCountFormControl.setValue(mainTriggerValue || 0);
    this.updateCameraRule();
  }

  updateTimeInterval() {
    const value =
      (parseInt((this.timeIntervalDayFormControl.value as any) || 0) || 0) * 1440 +
      (parseInt((this.timeIntervalHourFormControl.value as any) || 0) || 0) * 60 +
      (parseInt((this.timeIntervalMinuteFormControl.value as any) || 0) || 0);
    const mainTriggerValue = Math.max(
      this.minMainTriggerValue,
      Math.min(value, this.maxMainTriggerValue),
    );
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.mainTriggerValue = mainTriggerValue;
    this.rule.set(updated);
    this.timeIntervalDayFormControl.setValue(convertMainTriggerToTimeIntervalDay(mainTriggerValue));
    this.timeIntervalHourFormControl.setValue(
      convertMainTriggerToTimeIntervalHour(mainTriggerValue),
    );
    this.timeIntervalMinuteFormControl.setValue(
      convertMainTriggerToTimeIntervalMinute(mainTriggerValue),
    );
    this.updateCameraRule();
  }

  updateVitExpected() {
    let value = parseInt(this.vitExpectedFormControl.value);
    if (value === 0 || isNaN(value)) {
      value = 1;
    }
    const mainTriggerValue = Math.max(
      this.minMainTriggerValue,
      Math.min(value, this.maxMainTriggerValue),
    );
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.mainTriggerValue = mainTriggerValue;
    this.rule.set(updated);
    this.vitExpectedFormControl.setValue(
      convertMainTriggerToVitExpected(mainTriggerValue, updated.cameraScheduleRuleMainTrigger),
    );
    this.updateCameraRule();
  }

  updateProximityActiveTime() {
    const value =
      (parseInt(this.proximityActiveTimeFormControlDayFormControl.value as any) || 0) * 1440 +
      (parseInt(this.proximityActiveTimeFormControlHourFormControl.value as any) || 0) * 60 +
      (parseInt(this.proximityActiveTimeFormControlMinuteFormControl.value as any) || 0);
    const mainTriggerValue = Math.max(
      this.minMainTriggerValue,
      Math.min(value, this.maxMainTriggerValue),
    );
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.mainTriggerValue = mainTriggerValue;
    this.rule.set(updated);
    this.proximityActiveTimeFormControlDayFormControl.setValue(
      convertMainTriggerToProximityActiveTimeDay(mainTriggerValue),
    );
    this.proximityActiveTimeFormControlHourFormControl.setValue(
      convertMainTriggerToProximityActiveTimeHour(mainTriggerValue),
    );
    this.proximityActiveTimeFormControlMinuteFormControl.setValue(
      convertMainTriggerToProximityActiveTimeMinute(mainTriggerValue),
    );
    this.updateCameraRule();
  }

  updateAdditionalCondition(additionalCondition) {
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.ruleOnlyValidOnProximityState = additionalCondition?.includes('proximity');
    updated.ruleOnlyValidOnNoProximityState = additionalCondition?.includes('no proximity');
    updated.ruleOnlyValidInsideVirtualFence = additionalCondition?.includes('inside virtual fence');
    updated.ruleOnlyValidOutsideVirtualFence =
      additionalCondition?.includes('outside virtual fence');
    updated.ruleOnlyValidVitExpelledState = additionalCondition?.includes('vit expelled');
    updated.ruleOnlyValidVitNotExpelledState = additionalCondition?.includes('vit implanted');
    updated.ruleOnlyValidVitNoContactState = additionalCondition?.includes('vit no contact');
    updated.ruleOnlyValidVitContactState = additionalCondition?.includes('vit contact');
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onRepeatIntervalChanged(value: string | any) {
    this.repeatIntervalTypeFormControl.setValue(value);
    let cycleTime = this.rule().cycleTime;
    switch (value) {
      case 'daily':
        cycleTime = 60 * 24 * 60;
        break;
      case 'weekly':
        cycleTime = 60 * 24 * 7 * 60;
        break;
      case 'advanced':
        cycleTime = 60 * 24 * 7 * 60;
        const minutes = (cycleTime / 60) % 60;
        const days = Math.floor(cycleTime / (1440 * 60));
        const hours = (cycleTime / 60 - days * 1440 - minutes) / 60;
        this.repeatIntervalHourFormControl.setValue(hours);
        this.repeatIntervalMinuteFormControl.setValue(minutes);
        this.repeatIntervalDayFormControl.setValue(days);
        break;
    }
    const startTime =
      utcMoment(this.startDateFormControl.value)
        .set({
          hours: 0,
          minutes: 0,
        })
        .toISOString()
        ?.split('.')[0] + 'Z';
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.cycleTime = cycleTime;
    updated.onTime = cycleTime;
    updated.startTime = startTime;
    this.rule.set(updated);
    this.updateWeekdays();
    this.updateCameraRule();
  }

  onRepeatIntervalDayChanged(value: string | any) {
    const cycleTime =
      (parseInt(value) || 0) * 24 * 60 * 60 +
      (parseInt(this.repeatIntervalHourFormControl.value as any) || 0) * 60 * 60 +
      (parseInt(this.repeatIntervalMinuteFormControl.value as any) || 0) * 60;
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.cycleTime = cycleTime;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onRepeatIntervalHourChanged(value: string | any) {
    const cycleTime =
      (parseInt(this.repeatIntervalDayFormControl.value as any) || 0) * 24 * 60 * 60 +
      (parseInt(value) || 0) * 60 * 60 +
      (parseInt(this.repeatIntervalMinuteFormControl.value as any) || 0) * 60;
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.cycleTime = cycleTime;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onRepeatIntervalMinuteChanged(value: string | any) {
    const cycleTime =
      (parseInt(this.repeatIntervalDayFormControl.value as any) || 0) * 24 * 60 * 60 +
      (parseInt(this.repeatIntervalHourFormControl.value as any) || 0) * 60 * 60 +
      (parseInt(value) || 0) * 60;
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.cycleTime = cycleTime;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  onClickRemoveRule() {
    this.remove.emit(this.index());
  }

  isDeletable() {
    if (this.isRulesDeletable()) return true;
    return (
      this.cameraScheduleService.rules$.value &&
      Object.entries(this.cameraScheduleService.rules$.value)?.length > 1
    );
  }

  onStartWeekdayChanged(value: string | any) {
    if (this.lastStartWeekday !== value) {
      this.lastStartWeekday = value;
      this.updateStart();
    }
  }

  onEndWeekdayChanged(value: string | any) {
    if (this.lastEndWeekday !== value) {
      this.lastEndWeekday = value;
      this.updateEnd();
    }
  }

  updatePhotoInterval() {
    const photoInterval =
      (parseInt(this.photoIntervalMinuteFormControl.value as any) || 0) * 60 +
      (parseInt(this.photoIntervalSecondFormControl.value as any) || 0);
    const validPhotoInterval = Math.min(Math.max(photoInterval, 15), 180);
    if (photoInterval > 180 || photoInterval < 15) {
      this.photoIntervalMinuteFormControl.setValue(
        convertPictureIntervalToMinutes(validPhotoInterval),
      );
      this.photoIntervalSecondFormControl.setValue(
        convertPictureIntervalToSeconds(validPhotoInterval),
      );
    }
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.pictureInterval = validPhotoInterval;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  updateCameraFormat() {
    const cameraFormatId = parseInt(this.cameraFormatFormControl.value as any);
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.cameraFormatId = cameraFormatId;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  updatePriority() {
    const videoRecordMode = parseInt(this.priorityFormControl.value as any);
    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.videoRecordMode = videoRecordMode;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  getModeString() {
    if (!this.modeFormControl.value) {
      return '';
    }
    return MODE_OPTIONS.find((option) => option.value === this.modeFormControl.value)?.label;
  }

  getTriggerString() {
    if (!this.triggerFormControl.value) {
      return '';
    }
    const options = TRIGGER_OPTIONS(
      this.hasHighActivityCameraTrigger() ||
        this.remoteCommandsService.hasHighActivityCameraTrigger(),
    );
    return options.find((option) => option.value === this.triggerFormControl.value)?.label;
  }

  getDurationString() {
    if (!this.timeTypeFormControl.value) {
      return '';
    }
    if (this.timeTypeFormControl.value === 'permanent') {
      return 'Permanent';
    }
    const start = this.startDateFormControl.value
      ? utcMoment(this.startDateFormControl.value, true).format('YYYY-MM-DD')
      : '';
    const end = this.endDateFormControl.value
      ? utcMoment(this.endDateFormControl.value, true).format('YYYY-MM-DD')
      : '';
    return `${start} -> ${end}`;
  }

  getRepeatString() {
    if (!this.repeatIntervalTypeFormControl.value) {
      return '';
    }
    if (this.repeatIntervalTypeFormControl.value === 'daily') return 'Daily';
    if (this.repeatIntervalTypeFormControl.value === 'weekly') return 'Weekly';
    return this.getDurationStringInMinute(this.rule()?.cycleTime / 60);
  }

  getVitExpectedString() {
    if (!this.vitExpectedFormControl.value) {
      return '';
    }
    return this.VIT_TRIGGER_OPTIONS.find(
      (option) => option.value === parseInt(this.vitExpectedFormControl.value),
    )?.label;
  }

  getCameraFormatString(): string {
    if (
      this.cameraFormatFormControl.value === undefined ||
      this.cameraFormatFormControl.value === null ||
      this.cameraFormatFormControl.value === ''
    ) {
      return '';
    }
    if (this.modeFormControl.value === 'VIDEO') {
      return this.videoFormatsOptions()?.find(
        (option) => option.value === parseInt(this.cameraFormatFormControl.value),
      )?.label;
    }
    return this.photoFormatsOptions()?.find(
      (option) => option.value === parseInt(this.cameraFormatFormControl.value),
    )?.label;
  }

  getPriorityString() {
    const priority = this.priorityFormControl.value;
    if (priority == 1) return 'Standard';
    if (priority == 2) return 'High';
    return '';
  }

  getSizeString() {
    if (!this.modeFormControl.value) return '';
    if (this.modeFormControl.value === 'VIDEO') {
      return this.getDurationStringInSecond(this.rule().size);
    }
    const photoCount = this.photoCountFormControl.value
      ? `${this.photoCountFormControl.value}`
      : '';
    const interval =
      this.photoCountFormControl.value > 1
        ? ` / ${this.getDurationStringInSecond(this.rule().pictureInterval)}`
        : '';
    return `${photoCount} ${interval}`;
  }

  updateVideoLength() {
    let size =
      (parseInt((this.videoLengthHourFormControl.value as any) || '0') || 0) * 3600 +
      (parseInt((this.videoLengthMinuteFormControl.value as any) || '0') || 0) * 60 +
      (parseInt((this.videoLengthSecondFormControl.value as any) || '0') || 0);

    if (size < 5) {
      size = 5;
      this.videoLengthHourFormControl.setValue(0);
      this.videoLengthMinuteFormControl.setValue(0);
      this.videoLengthSecondFormControl.setValue(5);
    } else if (size > 3600) {
      size = 3600;
      this.videoLengthHourFormControl.setValue(1);
      this.videoLengthMinuteFormControl.setValue(0);
      this.videoLengthSecondFormControl.setValue(0);
    }

    const updated = cloneDeep(this.rule()) as dsDtos.CameraScheduleRule;
    updated.size = size;
    this.rule.set(updated);
    this.updateCameraRule();
  }

  getTimeIntervalString() {
    if (
      (!this.timeIntervalDayFormControl.value && this.timeIntervalDayFormControl.value != 0) ||
      (!this.timeIntervalHourFormControl.value && this.timeIntervalHourFormControl.value != 0) ||
      (!this.timeIntervalMinuteFormControl.value && this.timeIntervalMinuteFormControl.value != 0)
    ) {
      return '';
    }
    return this.getDurationStringInMinute(this.rule().mainTriggerValue);
  }

  getGpsSkipCountString() {
    if (!this.gpsSkipCountFormControl.value || isNaN(this.gpsSkipCountFormControl.value)) {
      return '0';
    }
    return this.gpsSkipCountFormControl.value;
  }

  getProximityActiveTime() {
    if (
      (!this.proximityActiveTimeFormControlDayFormControl.value &&
        this.proximityActiveTimeFormControlDayFormControl.value != 0) ||
      (!this.proximityActiveTimeFormControlHourFormControl.value &&
        this.proximityActiveTimeFormControlHourFormControl.value != 0) ||
      (!this.proximityActiveTimeFormControlMinuteFormControl.value &&
        this.proximityActiveTimeFormControlMinuteFormControl.value != 0)
    ) {
      return '';
    }
    return this.getDurationStringInMinute(this.rule().mainTriggerValue);
  }

  getTimeGpsString() {
    const additionalConditions = Array.from(
      new Set(this.additionalConditionOptionsFormControl.value),
    );
    if (!additionalConditions?.length) return '';
    if (additionalConditions.length > 1) {
      return additionalConditions.slice(0, 1).join() + ` + ${additionalConditions.length - 1}`;
    }
    return additionalConditions.join(`, `);
  }

  getHoverTimeGpsString() {
    const additionalConditions = Array.from(
      new Set(this.additionalConditionOptionsFormControl.value),
    );
    if (!additionalConditions?.length) return '';
    return additionalConditions.map(this.capitalizeFirstLetter).join(`, `);
  }

  capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  updateCameraRule() {
    this.cameraScheduleService.updateRule(this.index(), cloneDeep(this.rule()), this.readonly());
  }

  resetRule() {
    const start = utcMoment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const end = utcMoment(start).add({ year: 25 }).add({ second: 1 });
    this.rule.set({
      startTime: start.toISOString().split('.')[0] + 'Z',
      endTime: end.toISOString().split('.')[0] + 'Z',
      cycleTime: 1440 * 60,
      onTime: 1440 * 60,
      cameraScheduleRuleMainTrigger: 'TIMER',
      mainTriggerValue: 120,
    });
    this.startWeekdayFormControl.setValue('');
    this.endWeekdayFormControl.setValue('');
    this.repeatIntervalTypeFormControl.setValue('daily');
    this.endDateFormControl.setValue(end.toISOString().split('.')[0] + 'Z');
    this.startDateFormControl.setValue(start.toISOString().split('.')[0] + 'Z');
    this.startHourFormControl.setValue(0);
    this.startMinuteFormControl.setValue(0);
    this.endHourFormControl.setValue(24);
    this.endMinuteFormControl.setValue(0);
    this.photoCountFormControl.setValue(1);
    this.gpsSkipCountFormControl.setValue(0);
    this.vitExpectedFormControl.setValue(undefined);
    this.timeTypeFormControl.setValue('permanent');
    this.modeFormControl.setValue(undefined);
    this.triggerFormControl.setValue('TIMER');
    this.repeatIntervalDayFormControl.setValue(1);
    this.repeatIntervalHourFormControl.setValue(0);
    this.repeatIntervalMinuteFormControl.setValue(0);
    this.cameraFormatFormControl.setValue('');
    this.videoLengthHourFormControl.setValue(0);
    this.videoLengthMinuteFormControl.setValue(0);
    this.videoLengthSecondFormControl.setValue(10);
    this.photoIntervalMinuteFormControl.setValue(0);
    this.photoIntervalSecondFormControl.setValue(15);
    this.timeIntervalDayFormControl.setValue(0);
    this.timeIntervalHourFormControl.setValue(2);
    this.timeIntervalMinuteFormControl.setValue(0);
    this.proximityActiveTimeFormControlDayFormControl.setValue(0);
    this.proximityActiveTimeFormControlHourFormControl.setValue(0);
    this.proximityActiveTimeFormControlMinuteFormControl.setValue(0);
  }

  shouldShowStartAndEndTime() {
    return Boolean(this.timeTypeFormControl.value);
  }

  get minMainTriggerValue() {
    if (this.triggerFormControl.value === 'TIMER') return 1;
    return 0;
  }

  get maxMainTriggerValue() {
    if (this.triggerFormControl.value === 'GPS') return 15;
    if (this.triggerFormControl.value === 'VIT') return 3;
    return 65535;
  }

  ngOnDestroy() {
    this.rulesSubscription?.unsubscribe();
    this.removeSubscription?.unsubscribe();
  }

  protected readonly TIME_TYPE_OPTIONS = TIME_TYPE_OPTIONS;
  protected readonly REPEAT_INTERVAL_OPTIONS = REPEAT_INTERVAL_OPTIONS;
  protected readonly MODE_OPTIONS = MODE_OPTIONS;
  protected readonly TRIGGER_OPTIONS = TRIGGER_OPTIONS;
  protected readonly ADDITIONAL_CONDITION_TRIGGER_OPTIONS = ADDITIONAL_CONDITION_TRIGGER_OPTIONS;
  protected readonly VIT_TRIGGER_OPTIONS = VIT_TRIGGER_OPTIONS;
  protected readonly MIN_DATE = MIN_DATE;
  protected readonly MAX_DATE = MAX_DATE;
  protected isWeekly = isWeekly;
  protected isDaily = isDaily;
  protected getOffset = getOffset;
}
